import { FormControl, FormLabel } from '@mui/joy';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Input } from '@/design-system';
import { RunStatsSchema } from '@/types/schema';

import { ConversationFilterBar } from '../FilterBar/ConversationFilterBar';
import { InfoTooltip } from '../InfoTooltip/InfoTooltip';
import { SearchModel } from '../RunsTable/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../Select/Select';
import { SessionRuleForm, SessionRuleFormProps } from './SessionRuleForm';

const THREAD_IDLE_TIME_UNITS = ['minutes', 'hours', 'days'] as const;
type ThreadIdleTimeUnit = (typeof THREAD_IDLE_TIME_UNITS)[number];

export interface ThreadIdleTimeValue {
  value: number | null;
  unit: ThreadIdleTimeUnit;
}

export interface ThreadRuleFormProps
  extends Omit<SessionRuleFormProps, 'filterLabel' | 'renderFilters'> {
  threadFilterStats?: RunStatsSchema;
  threadIdleTime?: ThreadIdleTimeValue;
  onThreadIdleTimeChange: (idle: ThreadIdleTimeValue) => void;
}

export function ThreadRuleForm(props: ThreadRuleFormProps) {
  const {
    initialData,
    sessionId,
    onFormChange,
    filters,
    setFilters,
    threadFilterStats,
    threadIdleTime,
    onThreadIdleTimeChange,
  } = props;

  const [filterBarOpen, setFilterBarOpen] = useState(false);
  const [idleTimeValue, setIdleTimeValue] = useState<string>(() =>
    threadIdleTime?.value != null ? String(threadIdleTime.value) : ''
  );
  const [idleTimeUnit, setIdleTimeUnit] = useState<ThreadIdleTimeUnit>(
    threadIdleTime?.unit ?? 'hours'
  );

  useEffect(() => {
    if (threadIdleTime) {
      setIdleTimeValue(
        threadIdleTime.value != null ? String(threadIdleTime.value) : ''
      );
      setIdleTimeUnit(threadIdleTime.unit);
    }
  }, [threadIdleTime]);

  const handleIdleTimeChange = useCallback(
    (value: string, unit: ThreadIdleTimeUnit) => {
      setIdleTimeValue(value);
      setIdleTimeUnit(unit);

      const parsedValue = value === '' ? null : Number(value);
      const nextValue =
        parsedValue === null || Number.isNaN(parsedValue) ? null : parsedValue;
      onThreadIdleTimeChange({
        value: nextValue,
        unit,
      });
    },
    [onThreadIdleTimeChange]
  );

  const renderFilters = useCallback(
    ({
      filters: currentFilters,
      setFilters: updateFilters,
    }: {
      filters: SearchModel;
      setFilters: (filters: SearchModel) => void;
    }) => (
      <ConversationFilterBar
        open={filterBarOpen}
        setOpen={setFilterBarOpen}
        stats={threadFilterStats}
        value={currentFilters?.filter}
        onChange={(value) => {
          const nextFilters: SearchModel = currentFilters
            ? { ...currentFilters, filter: value }
            : { filter: value };
          updateFilters(nextFilters);
        }}
      />
    ),
    [filterBarOpen, threadFilterStats]
  );

  const idleTimeUnitLabel = useMemo(() => {
    switch (idleTimeUnit) {
      case 'minutes':
        return 'Minutes';
      case 'hours':
        return 'Hours';
      case 'days':
        return 'Days';
      default:
        return 'Hours';
    }
  }, [idleTimeUnit]);

  return (
    <div className="flex flex-col gap-4">
      <SessionRuleForm
        initialData={initialData}
        sessionId={sessionId}
        onFormChange={onFormChange}
        filters={filters}
        setFilters={setFilters}
        isCodeEvaluator={false}
        filterLabel="Thread Filters"
        renderFilters={renderFilters}
      />
      <div className="flex flex-col gap-3 pl-4">
        <FormControl>
          <div className="mb-1 flex items-center gap-2">
            <FormLabel sx={{ mb: 0 }}>Thread Idle Time</FormLabel>
            <InfoTooltip description="Setting this field will apply to all threads of this project." />
          </div>
          <div className="flex gap-2">
            <Input
              type="number"
              value={idleTimeValue}
              inputMode="numeric"
              onChange={(value) => {
                handleIdleTimeChange(value, idleTimeUnit);
              }}
              placeholder="Enter duration"
              min={0}
            />
            <Select
              value={idleTimeUnit}
              onValueChange={(unit) => {
                handleIdleTimeChange(idleTimeValue, unit as ThreadIdleTimeUnit);
              }}
            >
              <SelectTrigger className="min-w-[120px]">
                <SelectValue aria-label={idleTimeUnitLabel}>
                  {idleTimeUnitLabel}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {THREAD_IDLE_TIME_UNITS.map((unit) => (
                  <SelectItem key={unit} value={unit}>
                    {unit.charAt(0).toUpperCase() + unit.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </FormControl>
      </div>
    </div>
  );
}
